import { useMemo, useState } from 'react'
import { Dialog } from 'radix-ui'
import { X } from 'lucide-react'
import type { FieldDefinition } from '@/features/contract/types'
import type { SubjectType } from '../types'
import { useVerifyField } from '../hooks'
import { VerifyDialogClickSection } from './VerifyDialogClickSection'
import { VerifyDialogDocumentSection } from './VerifyDialogDocumentSection'
import { VerifyDialogCommentSection } from './VerifyDialogCommentSection'

export function VerifyDialog({
  field,
  subjectType,
  subjectId,
  currentValue,
  onClose,
}: {
  field: FieldDefinition
  subjectType: SubjectType
  subjectId: number
  currentValue: string | null
  onClose: () => void
}) {
  const requiredModes = field.verificationModes
  const verify = useVerifyField(subjectType, subjectId)
  const [clickConfirmed, setClickConfirmed] = useState(false)
  const [fileIds, setFileIds] = useState<number[]>([])
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)

  const canSubmit = useMemo(() => {
    if (requiredModes.includes('CLICK') && !clickConfirmed) return false
    if (requiredModes.includes('DOCUMENT') && fileIds.length === 0) return false
    if (requiredModes.includes('COMMENT') && comment.trim().length < 3) return false
    return true
  }, [requiredModes, clickConfirmed, fileIds, comment])

  const submit = () => {
    setError(null)
    verify.mutate(
      {
        fieldDefinitionId: field.id,
        subjectType,
        subjectId,
        modes: requiredModes,
        evidenceFileIds: requiredModes.includes('DOCUMENT') ? fileIds : undefined,
        evidenceComment: requiredModes.includes('COMMENT') ? comment.trim() : undefined,
      },
      {
        onSuccess: onClose,
        onError: (e: unknown) => setError(formatError(e)),
      },
    )
  }

  return (
    <Dialog.Root open onOpenChange={open => { if (!open) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-950/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded bg-white p-5 shadow-xl">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold text-slate-900">
              Zatwierdź pole „{field.labelPl}”
            </Dialog.Title>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700"
              aria-label="Zamknij"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3">
            {requiredModes.includes('CLICK') && (
              <VerifyDialogClickSection
                value={currentValue}
                checked={clickConfirmed}
                onCheckedChange={setClickConfirmed}
              />
            )}
            {requiredModes.includes('DOCUMENT') && (
              <VerifyDialogDocumentSection
                fileIds={fileIds}
                onFileIdsChange={setFileIds}
              />
            )}
            {requiredModes.includes('COMMENT') && (
              <VerifyDialogCommentSection
                value={comment}
                onChange={setComment}
              />
            )}
          </div>

          {error && <p className="mt-3 text-xs text-red-600">{error}</p>}

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit || verify.isPending}
              className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {verify.isPending ? 'Zatwierdzanie...' : 'Zatwierdź'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function formatError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response: { data?: { title?: string; detail?: string } } }).response
    return r?.data?.detail ?? r?.data?.title ?? 'Błąd zatwierdzania weryfikacji'
  }
  return 'Nieznany błąd'
}
